import type { ReactNode } from 'react';

export interface TabDescriptor<Id extends string = string> {
  id: Id;
  label: ReactNode;
}

export interface TabsProps<Id extends string = string> {
  tabs: readonly TabDescriptor<Id>[];
  value: Id;
  onChange: (id: Id) => void;
  /** Optional accessible name for the tablist. */
  'aria-label'?: string;
}

// Generic underlined tab strip (role=tablist/tab). Distinct from the
// request-scoped WorkspaceTabStrip. Controlled: caller owns the active id and
// renders the panel below. Labels flow in as props (no copy lives here).
export function Tabs<Id extends string = string>({
  tabs,
  value,
  onChange,
  'aria-label': ariaLabel,
}: TabsProps<Id>) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      style={{
        display: 'flex',
        gap: 'var(--s-2)',
        marginBottom: 'var(--s-4)',
        borderBottom: '1px solid var(--border-2)',
      }}
    >
      {tabs.map((t) => {
        const active = t.id === value;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.id)}
            style={{
              padding: 'var(--s-3) var(--s-4)',
              border: 'none',
              background: 'transparent',
              color: active ? 'var(--fg-1)' : 'var(--fg-3)',
              borderBottom: active ? '2px solid var(--accent-1)' : '2px solid transparent',
              cursor: 'pointer',
              fontSize: 'var(--fz-13)',
              fontWeight: 500,
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}
